import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import ActiveValues from '../../../../constants/ActiveValues';
import StatusCandidatePosition from '../../../../types/serverTypes/StatusCandidatePosition';

interface Props {
  value: ActiveValues;
  setSwitchingId: React.Dispatch<React.SetStateAction<number | null>>;
  candidatesCount: {
    new: number;
    shortlisted: number;
    declined: number;
    requestInterview: number;
    hired: number;
    offerSent: number;
    all: number;
  };
}

const SPKPositionStatusChip = ({
  statusName,
  statusAmmount,
  onClick,
  isActive = false,
}: {
  statusName: string;
  statusAmmount: number;
  onClick: () => void;
  isActive: boolean;
}) => {
  return (
    <span
      onClick={() => onClick()}
      aria-hidden
      className={`rounded-full flex items-center self-center px-4 py-1 hover:cursor-pointer ${
        isActive ? 'bg-spk-highContrast text-spk-softBlue' : 'bg-spk-neutral-40'
      }`}
    >
      {statusName}
      {statusAmmount !== 0 && <span>({statusAmmount})</span>}
    </span>
  );
};

export const SPKPositionStatusSwitch: FC<Props> = ({
  value,
  setSwitchingId,
  candidatesCount,
}) => {
  const [searchParams, setSeachParams] = useSearchParams();

  const [t] = useTranslation('global');

  const changeHandler = (positionStatus: ActiveValues) => {
    const paramsCopy = new URLSearchParams(searchParams);
    paramsCopy.set('cps', positionStatus);
    setSeachParams(paramsCopy);
    setSwitchingId(null);
  };

  const PositionStatusChips = useMemo(() => {
    return [
      {
        statusName: t('ChipGroup.New'),
        statusAmmount: candidatesCount[StatusCandidatePosition.new],
        activeValue: ActiveValues.New,
      },
      {
        statusName: t('ChipGroup.ShortList'),
        statusAmmount: candidatesCount[StatusCandidatePosition.shortlisted],
        activeValue: ActiveValues.ShortList,
      },
      {
        statusName: t('ChipGroup.BeingInterviewed'),
        statusAmmount:
          candidatesCount[StatusCandidatePosition.requestInterview],
        activeValue: ActiveValues.requestInterview,
      },
      {
        statusName: t('ChipGroup.OfferSent'),
        statusAmmount: candidatesCount[StatusCandidatePosition.offerSent],
        activeValue: ActiveValues.OfferSent,
      },
    ];
  }, [candidatesCount, t]);

  return (
    <div className="flex flex-row gap-2 max-w-full overflow-x-auto items-center">
      {PositionStatusChips.map((singleChip) => (
        <SPKPositionStatusChip
          key={`SPKPositionStatusChip_${singleChip.activeValue}`}
          statusName={singleChip.statusName}
          statusAmmount={singleChip.statusAmmount}
          onClick={() => changeHandler(singleChip.activeValue)}
          isActive={value === singleChip.activeValue}
        />
      ))}
    </div>
  );
};
